/**
 * Stable `contextKey` registry for the Lily Agent panel store / stream.
 *
 * Every panel mount picks a unique key so its conversation state is isolated
 * from other panels (e.g. the catalog-ops root panel doesn't share history with
 * the per-batch panel). Each feature owns its prefix; new prefixes are added
 * here so the type system sees them and IDE autocomplete keeps working.
 *
 * Format: `<feature>:<scope>[:<id>][:<view>]`
 *
 * The `string & {}` member at the end is an escape hatch — it accepts unknown
 * keys at runtime without forcing every new panel to update this type first,
 * but TypeScript still surfaces the known forms in autocomplete. Use the
 * `agentPanelContextKey` factory below when constructing keys so you get the
 * known prefixes for free and don't have to remember the exact format.
 */
export type AgentPanelContextKey =
  | 'catalog-ops:root'
  | `catalog-ops:batch:${string}`
  | 'onboarding:root'
  | `experiments:detail:${string}`
  | 'item-setup:catalog-review'
  | 'item-setup:taxonomy-review'
  | 'item-setup:output-format'
  | 'item-setup:source-priority'
  | 'item-setup:calibration-scope'
  | 'item-setup:content-generation'
  | 'item-setup:quality-report'
  | (string & {});

/**
 * Factory helpers for building well-known `AgentPanelContextKey` values.
 * Prefer these over raw string literals at call sites so the prefix is in
 * one place and refactors land cleanly.
 */
export const agentPanelContextKey = {
  /** Catalog-ops root dashboard panel. */
  catalogOpsRoot: 'catalog-ops:root' as const,
  /** Per-batch catalog-ops panel — `batchId` typically matches the URL slug. */
  catalogOpsBatch: (batchId: string) => `catalog-ops:batch:${batchId}` as const,
  /** Experiment-details chatbot panel (Phase 5+). */
  experimentsDetail: (experimentId: string) => `experiments:detail:${experimentId}` as const,
  /** Static onboarding panel (no chat). Reserved for the future chat-mode onboarding panel. */
  onboardingRoot: 'onboarding:root' as const,
  /** Item-setup catalog-review step — ingestion quality panel. */
  itemSetupCatalogReview: 'item-setup:catalog-review' as const,
  /** Item-setup taxonomy-review step — taxonomy/attribute review panel. */
  itemSetupTaxonomyReview: 'item-setup:taxonomy-review' as const,
  /** Item-setup output-format step — sample output confirmation panel. */
  itemSetupOutputFormat: 'item-setup:output-format' as const,
  /** Item-setup source-priority step (super-admin) — per-attribute source priority. */
  itemSetupSourcePriority: 'item-setup:source-priority' as const,
  /** Calibration step 1 — category scope selection panel. */
  itemSetupCalibrationScope: 'item-setup:calibration-scope' as const,
  /** Calibration step 2 — sample content generation panel. */
  itemSetupContentGeneration: 'item-setup:content-generation' as const,
  /** Calibration step 3 — sample quality report panel. */
  itemSetupQualityReport: 'item-setup:quality-report' as const,
} as const;
